import { create } from 'zustand';
import type { Camera, Command, HistoryState, WorkspaceDocument, WorkspaceId } from '@/types';
import { asWorkspaceId } from '@/types';
import { History } from '@/core/commands';
import { DEFAULT_CAMERA } from '@/constants/camera';
import { createId } from '@/utils/id';

export type DocumentCommand = Command<WorkspaceDocument>;

export interface WorkspaceStoreState {
  readonly workspaces: Readonly<Record<WorkspaceId, WorkspaceDocument>>;
  readonly activeWorkspaceId: WorkspaceId;
  /** Undo/redo history, isolated per workspace. */
  readonly histories: Readonly<Record<WorkspaceId, HistoryState<WorkspaceDocument>>>;
  /** Monotonic counter bumped on every document mutation; cheap change detection for subscribers. */
  readonly revision: number;
}

export interface WorkspaceStoreActions {
  readonly dispatch: (command: DocumentCommand) => void;
  /**
   * Applies a command to the document without recording history. For view-state
   * edits (window z-order on focus) that should persist but never appear as undo steps.
   */
  readonly apply: (command: DocumentCommand) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly createWorkspace: (name: string) => WorkspaceId;
  /** Adds a complete document (e.g. the showcase) with a fresh history and switches to it. */
  readonly importWorkspace: (doc: WorkspaceDocument) => void;
  readonly renameWorkspace: (id: WorkspaceId, name: string) => void;
  readonly deleteWorkspace: (id: WorkspaceId) => void;
  readonly switchWorkspace: (id: WorkspaceId) => void;
  /** Persists the live camera into the workspace record (called on debounce, not per frame). */
  readonly commitCamera: (camera: Camera) => void;
  /** Replaces all state (used by persistence on boot). */
  readonly hydrate: (state: Pick<WorkspaceStoreState, 'workspaces' | 'activeWorkspaceId'>) => void;
}

export type WorkspaceStore = WorkspaceStoreState & WorkspaceStoreActions;

export const createEmptyDocument = (id: WorkspaceId, name: string, now = Date.now()): WorkspaceDocument => ({
  workspace: { id, name, createdAt: now, updatedAt: now, camera: DEFAULT_CAMERA, bookmarks: [] },
  objects: {},
  connections: {},
});

const initialId = asWorkspaceId(createId('ws'));

const withUpdatedAt = (doc: WorkspaceDocument, now: number): WorkspaceDocument => ({
  ...doc,
  workspace: { ...doc.workspace, updatedAt: now },
});

export const useWorkspaceStore = create<WorkspaceStore>()((set, get) => ({
  workspaces: { [initialId]: createEmptyDocument(initialId, 'Workspace 1') },
  activeWorkspaceId: initialId,
  histories: { [initialId]: History.createHistory() },
  revision: 0,

  dispatch: (command) => {
    const { activeWorkspaceId, workspaces, histories, revision } = get();
    const doc = workspaces[activeWorkspaceId];
    if (!doc) return;
    const history = histories[activeWorkspaceId] ?? History.createHistory<WorkspaceDocument>();
    const now = Date.now();
    const result = History.execute(doc, history, command, now);
    // A no-op (e.g. moving only locked objects) leaves document, history and revision untouched.
    if (result.state === doc && result.history === history) return;
    set({
      workspaces: { ...workspaces, [activeWorkspaceId]: withUpdatedAt(result.state, now) },
      histories: { ...histories, [activeWorkspaceId]: result.history },
      revision: revision + 1,
    });
  },

  apply: (command) => {
    set((s) => {
      const doc = s.workspaces[s.activeWorkspaceId];
      if (!doc) return s;
      const next = command.execute(doc);
      if (next === doc) return s;
      return {
        workspaces: { ...s.workspaces, [s.activeWorkspaceId]: withUpdatedAt(next, Date.now()) },
        revision: s.revision + 1,
      };
    });
  },

  undo: () => {
    const { activeWorkspaceId, workspaces, histories, revision } = get();
    const doc = workspaces[activeWorkspaceId];
    const history = histories[activeWorkspaceId];
    if (!doc || !history || !History.canUndo(history)) return;
    const result = History.undo(doc, history);
    set({
      workspaces: { ...workspaces, [activeWorkspaceId]: withUpdatedAt(result.state, Date.now()) },
      histories: { ...histories, [activeWorkspaceId]: result.history },
      revision: revision + 1,
    });
  },

  redo: () => {
    const { activeWorkspaceId, workspaces, histories, revision } = get();
    const doc = workspaces[activeWorkspaceId];
    const history = histories[activeWorkspaceId];
    if (!doc || !history || !History.canRedo(history)) return;
    const result = History.redo(doc, history);
    set({
      workspaces: { ...workspaces, [activeWorkspaceId]: withUpdatedAt(result.state, Date.now()) },
      histories: { ...histories, [activeWorkspaceId]: result.history },
      revision: revision + 1,
    });
  },

  createWorkspace: (name) => {
    const id = asWorkspaceId(createId('ws'));
    set((s) => ({
      workspaces: { ...s.workspaces, [id]: createEmptyDocument(id, name) },
      histories: { ...s.histories, [id]: History.createHistory() },
      activeWorkspaceId: id,
      revision: s.revision + 1,
    }));
    return id;
  },

  importWorkspace: (doc) => {
    set((s) => ({
      workspaces: { ...s.workspaces, [doc.workspace.id]: doc },
      histories: { ...s.histories, [doc.workspace.id]: History.createHistory<WorkspaceDocument>() },
      activeWorkspaceId: doc.workspace.id,
      revision: s.revision + 1,
    }));
  },

  renameWorkspace: (id, name) => {
    set((s) => {
      const doc = s.workspaces[id];
      if (!doc) return s;
      return {
        workspaces: { ...s.workspaces, [id]: { ...doc, workspace: { ...doc.workspace, name } } },
        revision: s.revision + 1,
      };
    });
  },

  deleteWorkspace: (id) => {
    set((s) => {
      const ids = Object.keys(s.workspaces) as WorkspaceId[];
      if (ids.length <= 1 || !(id in s.workspaces)) return s;
      const { [id]: _removed, ...workspaces } = s.workspaces;
      const { [id]: _removedHistory, ...histories } = s.histories;
      const fallback = ids.find((x) => x !== id) ?? s.activeWorkspaceId;
      return {
        workspaces,
        histories,
        activeWorkspaceId: s.activeWorkspaceId === id ? fallback : s.activeWorkspaceId,
        revision: s.revision + 1,
      };
    });
  },

  switchWorkspace: (id) => {
    set((s) => (id in s.workspaces && id !== s.activeWorkspaceId ? { activeWorkspaceId: id } : s));
  },

  commitCamera: (camera) => {
    set((s) => {
      const doc = s.workspaces[s.activeWorkspaceId];
      if (!doc) return s;
      const c = doc.workspace.camera;
      if (c.zoom === camera.zoom && c.position.x === camera.position.x && c.position.y === camera.position.y) return s;
      return {
        workspaces: {
          ...s.workspaces,
          [s.activeWorkspaceId]: { ...doc, workspace: { ...doc.workspace, camera } },
        },
      };
    });
  },

  hydrate: ({ workspaces, activeWorkspaceId }) => {
    set((s) => {
      const ids = Object.keys(workspaces) as WorkspaceId[];
      const [first] = ids;
      if (first === undefined) return s;
      const histories = Object.fromEntries(ids.map((id) => [id, History.createHistory<WorkspaceDocument>()]));
      return {
        workspaces,
        histories,
        activeWorkspaceId: activeWorkspaceId in workspaces ? activeWorkspaceId : first,
        revision: s.revision + 1,
      };
    });
  },
}));

/* ---------- Selectors (stable references, safe for useStore) ---------- */

export const selectActiveDocument = (s: WorkspaceStoreState): WorkspaceDocument | undefined =>
  s.workspaces[s.activeWorkspaceId];

export const selectActiveObjects = (s: WorkspaceStoreState): WorkspaceDocument['objects'] | undefined =>
  s.workspaces[s.activeWorkspaceId]?.objects;

export const selectCanUndo = (s: WorkspaceStoreState): boolean => {
  const h = s.histories[s.activeWorkspaceId];
  return h ? History.canUndo(h) : false;
};

export const selectCanRedo = (s: WorkspaceStoreState): boolean => {
  const h = s.histories[s.activeWorkspaceId];
  return h ? History.canRedo(h) : false;
};
